import React, { useEffect, useState } from 'react';
import ciclii3 from '../../assets/ciclii3.png';
import { useAuth } from '../../context/AuthContext';
import { CreateEmotionDto, EmotionDto } from '../../interfaces/emotion';
import { FlowDto } from '../../interfaces/flow';
import { PeriodDto } from '../../interfaces/period';
import { CreateSymptomRequestDto, SymptomDto } from '../../interfaces/symptom';
import { emotionService } from '../../services/EmotionService';
import { flowService } from '../../services/FlowService';
import { periodService } from '../../services/PeriodService';
import { symptomService } from '../../services/SymptomService';

interface FirstTimeFormData {
    hasHadFirstBleeding: boolean | null;
    durationDays: string;
    startDate: string;
    endDate: string;
    flowLevel: FlowDto | null;
    symptoms: number[];
    emotions: number[];
}

interface FirstTimeFormProps {
    setCheckPeriods: (value: boolean) => void;
    setShowForm: (value: boolean) => void;
    showForm?: boolean;
    onSubmit?: (data: FirstTimeFormData) => void;
    onSkipFirstPeriod?: () => void;
}

const FirstTimeForm: React.FC<FirstTimeFormProps> = ({ setCheckPeriods, setShowForm, showForm = true, onSubmit, onSkipFirstPeriod }) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [formData, setFormData] = useState<FirstTimeFormData>({
        hasHadFirstBleeding: null,
        durationDays: '',
        startDate: '',
        endDate: '',
        flowLevel: null,
        symptoms: [],
        emotions: [],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateErrors, setDateErrors] = useState<{ startDate: string | null; endDate: string | null }>({
        startDate: null,
        endDate: null,
    });
    const [flujosCatalogo, setflujosCatalogo] = useState<FlowDto[]>([]);
    const [sintomasCatalogo, setSintomasCatalogo] = useState<SymptomDto[]>([]);
    const [emocionesCatalogo, setEmocionesCatalogo] = useState<EmotionDto[]>([]);
    const [lastPeriod, setLastPerdiod] = useState<PeriodDto | null>(null);
    const [sintomasSelected, setSintomasSelected] = useState<CreateSymptomRequestDto[]>([]);
    const [emocionesSelected, setEmocionesSelected] = useState<CreateEmotionDto[]>([]);

    const { user } = useAuth();


    const handleFirstBleedingChange = (hasHadFirstBleeding: boolean) => {
        setFormData(prev => ({ ...prev, hasHadFirstBleeding }));
        setDateErrors({ startDate: null, endDate: null });

        if (!hasHadFirstBleeding) {
            setShowForm(false);
            setCheckPeriods(false);
            setCurrentStep(0);
            onSkipFirstPeriod?.();
            return;
        }

        setError(null);
        setCurrentStep(1);
    };

    const handleNextStep = () => {
        setCurrentStep(prev => prev + 1);
    };

    // Nueva función para guardar periodo y flujo
    const savePeriod = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!user?.id_usuario) throw new Error('No se encontró información del usuario');

            const periodPayload = {
                id_usuario: user.id_usuario,
                fecha_inicio: formData.startDate,
                fecha_termino: formData.endDate || undefined,
            };

            const response = await periodService.createPeriod(periodPayload);

            if (response.statusCode === 201) {
                const lastPeriodo = response.periodos[response.periodos.length - 1];
                setLastPerdiod(lastPeriodo);
                handleNextStep();
            } else {
                setError(response.message || 'No se pudo guardar el periodo.');
            }
        } catch (err: any) {
            setError(err.message || 'No se pudo guardar el periodo. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    // Función local para crear flujo asociado a un periodo
    const saveFlow = async () => {
        console.log('saveFlow: lastPeriod =', lastPeriod);
        if (!lastPeriod) return;
        const flujoPayload = {
            id_periodo: lastPeriod.id_periodo,
            id_flujo: formData.flowLevel ? formData.flowLevel.id_flujo : 1,
            fecha: lastPeriod.fecha_termino || lastPeriod.fecha_inicio,
        };
        setLoading(true);
        setError(null);
        try {
            const flow = await flowService.createFlowOfPeriod(flujoPayload);
            console.log('Respuesta de flowService.createFlowOfPeriod:', flow);
            if (flow.statusCode === 201) {
                console.log('Antes de handleNextStep, currentStep:', currentStep);
                handleNextStep();
                setTimeout(() => {
                    console.log('Después de handleNextStep, currentStep:', currentStep);
                }, 0);
            } else {
                setError('No se pudo guardar el flujo');
            }
        } catch (err) {
            setError('No se pudo crear el flujo');
        } finally {
            setLoading(false);
        }
    };

    const handleSymptomSelection = (id_sintoma: number) => {
        if (!lastPeriod) return;

        const symptomPayload: CreateSymptomRequestDto = {
            id_periodo: lastPeriod.id_periodo,
            fecha: lastPeriod.fecha_termino || lastPeriod.fecha_inicio,
            id_sintoma: id_sintoma,
            intensidad: 1,
            notas: '',
        };

        setSintomasSelected(prev => {
            const exists = prev.find(s => s.id_sintoma === id_sintoma);
            if (exists) {
                // Desmarcar: quitar del array
                return prev.filter(s => s.id_sintoma !== id_sintoma);
            } else {
                // Marcar: agregar al array
                return [...prev, symptomPayload];
            }
        });

        // También actualizar el formData para mantener consistencia
        handleSymptomToggle(id_sintoma);
    };

    const saveAllSymptoms = async () => {
        if (sintomasSelected.length === 0) {
            handleNextStep();
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await symptomService.createSymptom(sintomasSelected);
            if (response.statusCode !== 201) {
                throw new Error(`No se pudieron guardar los síntomas: ${response.message}`);
            }
            handleNextStep();
        } catch (err: any) {
            setError(err.message || 'No se pudieron guardar los síntomas');
        } finally {
            setLoading(false);
        }
    };

    const handleEmotionSelection = (id_emocion: number) => {
        if (!lastPeriod) return;

        const emotionPayload: CreateEmotionDto = {
            id_periodo: lastPeriod.id_periodo,
            fecha: lastPeriod.fecha_termino || lastPeriod.fecha_inicio,
            id_emocion: id_emocion,
        };

        setEmocionesSelected(prev => {
            const exists = prev.find(e => e.id_emocion === id_emocion);
            if (exists) {
                // Desmarcar: quitar del array
                return prev.filter(e => e.id_emocion !== id_emocion);
            } else {
                // Marcar: agregar al array
                return [...prev, emotionPayload];
            }
        });

        // También actualizar el formData para mantener consistencia
        handleEmotionToggle(id_emocion);
    };

    const saveAllEmotions = async () => {
        if (emocionesSelected.length === 0) {
            setShowForm(false);
            setCheckPeriods(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await emotionService.createEmotion(emocionesSelected);
            if (response.statusCode !== 201) {
                throw new Error(`No se pudieron guardar las emociones: ${response.message}`);
            }
            setShowForm(false);
            setCheckPeriods(true);
        } catch (err: any) {
            setError(err.message || 'No se pudieron guardar las emociones');
        } finally {
            setLoading(false);
        }
    };

    const handleSymptomToggle = (id_sintoma: number) => {
        setFormData(prev => ({
            ...prev,
            symptoms: prev.symptoms.includes(id_sintoma)
                ? prev.symptoms.filter(s => s !== id_sintoma)
                : [...prev.symptoms, id_sintoma],
        }));
    };

    const handleEmotionToggle = (id_emocion: number) => {
        setFormData(prev => ({
            ...prev,
            emotions: prev.emotions.includes(id_emocion)
                ? prev.emotions.filter(e => e !== id_emocion)
                : [...prev.emotions, id_emocion],
        }));
    };

    // (Eliminada la versión antigua de handleSubmit, solo queda la async)

    const isCompleteValidDate = (value: string) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return false;
        }

        const [yearStr] = value.split('-');
        if (!yearStr || yearStr.startsWith('0') || Number(yearStr) < 1000) {
            return false;
        }

        return true;
    };

    const computeDateErrors = (state: FirstTimeFormData) => {
        const errors: { startDate: string | null; endDate: string | null } = {
            startDate: null,
            endDate: null,
        };

        const { startDate, endDate } = state;
        const startComplete = isCompleteValidDate(startDate);
        const endComplete = isCompleteValidDate(endDate);

        if (startComplete && startDate > maxDate) {
            errors.startDate = 'No puedes seleccionar una fecha futura.';
        }

        if (endComplete && endDate > maxDate) {
            errors.endDate = 'No puedes seleccionar una fecha futura.';
        }

        if (startComplete && endComplete && endDate < startDate) {
            if (!errors.startDate) {
                errors.startDate = 'La fecha de inicio no puede ser posterior a la fecha de finalización.';
            }
            if (!errors.endDate) {
                errors.endDate = 'La fecha de finalización no puede ser anterior a la fecha de inicio.';
            }
        }

        return errors;
    };

    const handleInputChange = (field: keyof FirstTimeFormData, value: any) => {
        if (field === 'flowLevel') {
            setFormData(prev => ({ ...prev, flowLevel: value }));
            return;
        }

        if (field === 'startDate' || field === 'endDate') {
            setFormData(prev => {
                const nextValue = value ?? '';
                const nextState = { ...prev, [field]: nextValue } as FirstTimeFormData;

                setDateErrors(computeDateErrors(nextState));

                return nextState;
            });
            return;
        }

        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Fecha máxima local para los inputs date
    const todayLocal = new Date();
    todayLocal.setMinutes(todayLocal.getMinutes() - todayLocal.getTimezoneOffset());
    const maxDate = todayLocal.toISOString().slice(0, 10);

    const getFlujos = async () => {
        try {
            const res = await flowService.getFlows();
            if (res.statusCode === 200) {
                setflujosCatalogo(Array.isArray(res.flujo) ? res.flujo : []);
            }
            return res;
        } catch (err) {
            console.error('Error al obtener flujos:', err);
            setflujosCatalogo([]);
            return null;
        }
    };

    const getSymptoms = async () => {
        try {
            const res = await symptomService.getSymptoms();
            if (res.statusCode === 200) {
                setSintomasCatalogo(Array.isArray(res.sintoma) ? res.sintoma : []);
            }
            return res;
        } catch (err) {
            console.error('Error al obtener síntomas:', err);
            setflujosCatalogo([]);
            return null;
        }
    };

    const getEmotions = async () => {
        try {
            const res = await emotionService.getEmotions();
            if (res.statusCode === 200) {
                setEmocionesCatalogo(Array.isArray(res.emocion) ? res.emocion : []);
            }
            return res;
        } catch (err) {
            console.error('Error al obtener emociones:', err);
            setflujosCatalogo([]);
            return null;
        }
    };



    useEffect(() => {
        getFlujos();
        getSymptoms();
        getEmotions();
    }, []);



    // Función para renderizar cada paso
    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className='first-time-question'>
                        {/* <img src={ciclii3} alt='Ciclii' style={{ maxHeight: 150, alignSelf: 'center' }} />
                        <h2>¡Bienvenida a CICLII!</h2>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <p className='' style={{ marginTop: 20, width: '100%', maxWidth: 500, alignSelf: 'center' }}>Para comenzar a gestionar tu ciclo menstrual, necesitamos hacerte algunas preguntas. Si deseas responder más tarde, puedes omitir esta pregunta presionando la opción "No".</p>
                        </div> */}
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                            <h2>¡Bienvenida a CICLII!</h2>
                            <img src={ciclii3} alt='Ciclii' style={{ maxHeight: 50 }} />
                        </div>
                        <p className='question-text'>¿Ya tuviste tu primer sangrado menstrual?</p>
                        <div className='checkbox-options'>
                            <label className='checkbox-option' style={{ minWidth: 100, textAlign: 'center', justifyContent: 'center', display: 'flex' }}>
                                <input
                                    type='radio'
                                    name='firstBleeding'
                                    onChange={() => handleFirstBleedingChange(true)}
                                    checked={formData.hasHadFirstBleeding === true}
                                    style={{ display: 'none' }}
                                />
                                <span className='checkmark'></span>
                                Sí
                            </label>
                            <label className='checkbox-option' style={{ minWidth: 100, textAlign: 'center', justifyContent: 'center', display: 'flex' }}>
                                <input
                                    type='radio'
                                    name='firstBleeding'
                                    onChange={() => handleFirstBleedingChange(false)}
                                    checked={formData.hasHadFirstBleeding === false}
                                    style={{ display: 'none' }}
                                />
                                <span className='checkmark'></span>
                                No
                            </label>
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className='step-container'>
                        <h2>¿Recuerdas entre qué fechas ocurrió tu primer sangrado? 🗓️</h2>
                        <div>Si no recuerdas una fecha exacta, ingresa la que más se acerque </div>
                        <br />
                        <div className='form-group'>
                            <label htmlFor='startDate'>Fecha de inicio</label>
                            <input
                                type='date'
                                id='startDate'
                                value={formData.startDate}
                                onChange={(e) => handleInputChange('startDate', e.target.value)}
                                required
                                max={maxDate}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') e.preventDefault();
                                }}
                            />
                            {dateErrors.startDate && (
                                <p style={{ color: '#d32f2f', fontSize: '0.9rem', marginTop: 8 }} role='alert'>
                                    {dateErrors.startDate}
                                </p>
                            )}
                        </div>
                        <div className='form-group'>
                            <label htmlFor='endDate'>Fecha de finalización</label>
                            <input
                                type='date'
                                id='endDate'
                                value={formData.endDate}
                                onChange={(e) => handleInputChange('endDate', e.target.value)}
                                required
                                style={{ width: '100%' }}
                                max={maxDate}
                                min={formData.startDate || undefined}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') e.preventDefault();
                                }}
                            />
                            <div style={{ marginTop: 8 }}>
                                <label className='menstruating-checkbox' style={{ flexDirection: 'row', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <input
                                        type='checkbox'
                                        checked={formData.endDate === maxDate}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                handleInputChange('endDate', maxDate);
                                            } else {
                                                handleInputChange('endDate', '');
                                            }
                                        }}
                                    />
                                    <span className='custom-checkbox'></span>
                                    <span className='label-text'>Aun estoy menstruando</span>
                                </label>
                            </div>
                            {dateErrors.endDate && (
                                <p style={{ color: '#d32f2f', fontSize: '0.9rem', marginTop: 8 }} role='alert'>
                                    {dateErrors.endDate}
                                </p>
                            )}
                        </div>
                        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                        <div className='step-navigation'>
                            {/* <button type='button' className='btn btn-secondary' onClick={handlePrevStep}>
                                Anterior
                            </button> */}
                            <button
                                type='button'
                                className={`btn btn-primary${loading ? ' btn-loading' : ''}`}
                                onClick={savePeriod}
                                disabled={loading || !formData.startDate || !formData.endDate}
                            >
                                <span style={{ visibility: loading ? 'hidden' : 'visible' }}>Continuar</span>
                            </button>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className='step-container'>
                        <h2>¿Recuerdas cómo fue tu flujo?</h2>
                        <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
                            Selecciona las gotitas según la intensidad de tu flujo
                        </p>

                        <div className='flow-selector'>
                            {flujosCatalogo.map((level, index) => {
                                const isSelected = formData.flowLevel && formData.flowLevel.id_flujo === level.id_flujo;
                                const isActive = formData.flowLevel && formData.flowLevel.id_flujo >= level.id_flujo;
                                return (
                                    <div
                                        key={level.id_flujo}
                                        className={`droplet ${isSelected ? 'selected' : ''} ${isActive ? 'active' : ''}`}
                                        onClick={() => handleInputChange('flowLevel', level)}
                                        title={level.descripcion}
                                    >
                                        <span style={{ visibility: 'hidden' }}>•</span>
                                    </div>
                                );
                            })}
                        </div>

                        {formData.flowLevel && (
                            <>
                                <div className='flow-level-text'>
                                    {formData.flowLevel.nivel_flujo}
                                </div>
                                <div className='flow-description'>
                                    {formData.flowLevel.descripcion}
                                </div>
                            </>
                        )}
                        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                        <div className='step-navigation'>
                            {/* <button type='button' className='btn-secondary' disabled onClick={handlePrevStep}>
                                Anterior
                            </button> */}
                            <button
                                type='button'
                                className={`btn btn-primary${loading ? ' btn-loading' : ''}`}
                                onClick={saveFlow}
                                disabled={loading || !formData.flowLevel}
                            >
                                <span style={{ visibility: loading ? 'hidden' : 'visible' }}>Continuar</span>
                            </button>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className='step-container'>
                        <h2>¿Recuerdas qué síntomas experimentaste? 🤒</h2>
                        <div className='button-grid'>
                            {sintomasCatalogo.map(sintoma => {
                                const id = Number(sintoma.id_sintoma);
                                return (
                                    <button
                                        key={id}
                                        type='button'
                                        className={`option-button ${formData.symptoms.includes(id) ? 'selected' : ''}`}
                                        onClick={() => handleSymptomSelection(id)}
                                    >
                                        {sintoma.nombre_sintoma}
                                    </button>
                                );
                            })}
                        </div>
                        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                        <div className='step-navigation'>
                            {/* <button type='button' className='btn-secondary' disabled onClick={handlePrevStep}>
                                Anterior
                            </button> */}
                            <button
                                type='button'
                                className={`btn btn-primary${loading ? ' btn-loading' : ''}`}
                                onClick={saveAllSymptoms}
                                disabled={loading}
                            >
                                <span style={{ visibility: loading ? 'hidden' : 'visible' }}>Continuar</span>
                            </button>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className='step-container'>
                        <h2>¿Recuerdas qué emociones experimentaste? 😊</h2>
                        <div className='button-grid'>
                            {emocionesCatalogo.map(emocion => {
                                const id = Number(emocion.id_emocion);
                                return (
                                    <button
                                        key={id}
                                        type='button'
                                        className={`option-button ${formData.emotions.includes(id) ? 'selected' : ''}`}
                                        onClick={() => handleEmotionSelection(id)}
                                    >
                                        {emocion.nombre_emocion}
                                    </button>
                                );
                            })}
                        </div>
                        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                        <div className='step-navigation'>
                            {/* <button type='button' className='btn-secondary' onClick={handlePrevStep} disabled={loading}>
                                Anterior
                            </button> */}
                            <button
                                type='button'
                                className='btn-primary'
                                disabled={loading}
                                style={{ marginLeft: 8 }}
                                onClick={saveAllEmotions}
                            >
                                {loading ? 'Guardando...' : 'Guardar información'}
                            </button>
                        </div>
                    </div>
                );

            default:
                return renderStep();
        }
    };

    const containerClassName = currentStep === 0 ? 'first-time-question-wrapper' : 'first-time-container';

    return (
        <div className={containerClassName}>
            {currentStep > 0 && (
                <>
                    <div className='progress-bar'>
                        <div className='progress-fill' style={{ width: `${(currentStep / 4) * 100}%` }}></div>
                    </div>
                    <div className='step-indicator'>
                        Paso {currentStep} de 4
                    </div>
                </>
            )}
            {renderStep()}
        </div>
    );
};

export default FirstTimeForm;